import { useState } from "react";
import { toast } from "sonner";
import { Flame, PiggyBank, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const eur = (v) => (v == null ? "—" : `€${Number(v).toLocaleString("de-DE", { maximumFractionDigits: 0 })}`);
const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`);

function CostCell({ c, onSaved }) {
  const [v, setV] = useState(c.cost ? String(c.cost) : "");
  const save = async () => {
    if (Number(v || 0) === Number(c.cost || 0)) return;
    try {
      await api.put(`/marketing/intel/campaigns/${c.id}/cost`, { cost: Number(v || 0) });
      toast.success("Cost saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return <Input type="number" data-testid={`mkt-cost-${c.id}`} className="h-7 w-24 text-xs font-mono text-right" placeholder="cost" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} />;
}

// Campaign ROI & attribution: cost, leads, won revenue, CPL, ROI per campaign + lead sources.
export function CampaignRoi({ month }) {
  const q = useApi(`/marketing/intel/roi?month=${month}`, [month]);
  const d = q.data;
  if (!d) return null;
  const t = d.totals;
  return (
    <div className="border rounded-lg bg-card overflow-x-auto mb-4" data-testid="mkt-roi">
      <div className="px-3 py-2 flex flex-wrap items-center gap-3">
        <span className="eyebrow flex items-center gap-1"><Target className="h-3 w-3" />Campaign ROI · {d.month}</span>
        <span className="ml-auto text-xs flex gap-3"><span>Spend <b>{eur(t.cost)}</b></span><span>Attributed revenue <b className="text-emerald-700">{eur(t.revenue)}</b></span><span>Leads <b>{t.leads}</b></span><span>CPL <b>{eur(t.cpl)}</b></span><span>ROI <b data-testid="mkt-roi-total" className={t.roi_pct != null && t.roi_pct < 0 ? "text-red-700" : "text-emerald-700"}>{pct(t.roi_pct)}</b></span></span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase font-mono text-muted-foreground"><tr><th className="px-3 py-1 text-left">Campaign</th><th className="px-3 py-1 text-left">Channel</th><th className="px-3 py-1 text-right">Sent · clicks</th><th className="px-3 py-1 text-right">Leads · won</th><th className="px-3 py-1 text-right">Revenue</th><th className="px-3 py-1 text-right">Cost</th><th className="px-3 py-1 text-right">CPL</th><th className="px-3 py-1 text-right">ROI</th></tr></thead>
        <tbody className="divide-y">
          {d.campaigns.map((c) => (
            <tr key={c.id} data-testid={`mkt-roi-${c.id}`}>
              <td className="px-3 py-1.5 font-medium">{c.name}</td><td className="px-3 py-1.5 text-xs">{c.channel}</td>
              <td className="px-3 py-1.5 text-right font-mono text-xs">{c.sent} · {c.clicks}</td><td className="px-3 py-1.5 text-right font-mono text-xs">{c.leads} · {c.won}</td>
              <td className="px-3 py-1.5 text-right font-mono text-xs">{eur(c.revenue)}</td><td className="px-3 py-1.5 text-right"><CostCell c={c} onSaved={q.reload} /></td>
              <td className="px-3 py-1.5 text-right font-mono text-xs">{eur(c.cpl)}</td><td className={`px-3 py-1.5 text-right font-mono text-xs ${c.roi_pct != null && c.roi_pct < 0 ? "text-red-700" : "text-emerald-700"}`}>{pct(c.roi_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.campaigns.length === 0 ? <div className="px-3 py-3 text-xs text-muted-foreground">No campaigns yet.</div> : null}
      <div className="px-3 py-2 border-t text-xs flex flex-wrap gap-3" data-testid="mkt-sources"><span className="eyebrow">Lead sources</span>{d.sources.map((s) => <span key={s.source}><b>{s.source}</b> {s.leads} leads · {s.won} won · {eur(s.revenue)}</span>)}{d.sources.length === 0 ? <span className="text-muted-foreground">no leads this month</span> : null}</div>
      <div className="px-3 pb-2 text-[11px] text-muted-foreground">Attribution: leads created this month that carry the campaign (campaign_id / UTM / source = campaign name). Enter campaign cost to get CPL and ROI.</div>
    </div>
  );
}

function BudgetCell({ r, month, onSaved }) {
  const [v, setV] = useState(r.budget == null ? "" : String(r.budget));
  const save = async () => {
    if (v === "" || Number(v) === r.budget) return;
    try {
      await api.put("/marketing/intel/budget", { month, channel: r.channel, budget: Number(v) });
      toast.success(`${r.channel} budget saved`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return <Input type="number" data-testid={`mkt-budget-${r.channel}`} className="h-7 w-24 text-xs font-mono text-right" placeholder="budget" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} />;
}

// Budget vs spend per channel with variance.
export function ChannelBudget({ month, canEdit }) {
  const q = useApi(`/marketing/intel/budget?month=${month}`, [month]);
  const d = q.data;
  if (!d) return null;
  return (
    <div className="border rounded-lg bg-card p-3 mb-4" data-testid="mkt-budget">
      <div className="eyebrow flex items-center gap-1 mb-2"><PiggyBank className="h-3 w-3" />Budget per channel · {d.month}<span className="ml-auto normal-case tracking-normal font-sans text-xs">Budget <b>{eur(d.total_budget)}</b> · Spend <b>{eur(d.total_spend)}</b></span></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {d.rows.map((r) => (
          <div key={r.channel} data-testid={`mkt-budget-row-${r.channel}`} className={`border rounded-md px-2 py-1.5 text-xs ${r.over ? "border-red-300 bg-red-50" : ""}`}>
            <div className="flex items-center justify-between"><b className="capitalize">{r.channel}</b>{r.used_pct != null ? <span className={`font-mono ${r.over ? "text-red-700" : "text-muted-foreground"}`}>{r.used_pct}%</span> : null}</div>
            <div className="flex items-center justify-between mt-1"><span>spend {eur(r.spend)}</span>{canEdit ? <BudgetCell r={r} month={month} onSaved={q.reload} /> : <span className="font-mono">{eur(r.budget)}</span>}</div>
            {r.variance != null ? <div className={`mt-0.5 ${r.variance < 0 ? "text-red-700" : "text-emerald-700"}`}>{r.variance < 0 ? "over by " : "left "}{eur(Math.abs(r.variance))}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

// Lead scoring with hot-lead routing.
export function LeadScores({ canRoute }) {
  const q = useApi("/marketing/intel/lead-scores?limit=25");
  const [busy, setBusy] = useState(false);
  const d = q.data;
  if (!d) return null;
  const route = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/intel/lead-scores/route");
      toast.success(data.routed.length ? `${data.routed.length} hot lead${data.routed.length > 1 ? "s" : ""} routed to sales` : "No unassigned hot leads");
      q.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const tone = { hot: "bg-red-50 text-red-700", warm: "bg-amber-50 text-amber-700", cold: "bg-slate-100 text-slate-600" };
  return (
    <div className="border rounded-lg bg-card overflow-x-auto mb-4" data-testid="mkt-lead-scores">
      <div className="px-3 py-2 flex flex-wrap items-center gap-3">
        <span className="eyebrow flex items-center gap-1"><Flame className="h-3 w-3" />Lead scoring</span>
        <span className="text-xs"><b className="text-red-700">{d.bands.hot} hot</b> · <b className="text-amber-700">{d.bands.warm} warm</b> · {d.bands.cold} cold · {d.total} open</span>
        {canRoute ? <Button size="sm" className="h-7 ml-auto bg-red-700 hover:bg-red-800" data-testid="mkt-route-hot" disabled={busy} onClick={route}><Flame className="h-3 w-3 mr-1" />Route unassigned hot leads to sales</Button> : null}
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase font-mono text-muted-foreground"><tr><th className="px-3 py-1 text-left">Lead</th><th className="px-3 py-1 text-left">Source</th><th className="px-3 py-1 text-right">Value</th><th className="px-3 py-1 text-right">Score</th><th className="px-3 py-1 text-left">Why</th></tr></thead>
        <tbody className="divide-y">
          {d.rows.map((r) => (
            <tr key={r.id} data-testid={`mkt-lead-${r.id}`}>
              <td className="px-3 py-1.5"><div className="font-medium">{r.name}</div><div className="text-[11px] text-muted-foreground">{r.contact_name}{r.owner_user_id ? "" : " · unassigned"}</div></td>
              <td className="px-3 py-1.5 text-xs">{r.source || "—"}</td><td className="px-3 py-1.5 text-right font-mono text-xs">{eur(r.expected_revenue)}</td>
              <td className="px-3 py-1.5 text-right"><span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${tone[r.band]}`}>{r.score} {r.band}</span></td>
              <td className="px-3 py-1.5 text-[11px] text-muted-foreground">{r.reasons.join(", ") || "no signals yet"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 pb-2 text-[11px] text-muted-foreground">Signals: e-mail opens/clicks, sales visits, surveys, events, recency and deal value. Hot ≥ 60, warm ≥ 30.</div>
    </div>
  );
}
